import { inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { collection, collectionData, doc, docData, Firestore, getFirestore, orderBy, query, where } from '@angular/fire/firestore';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';
import { AuthService } from '../../auth.service';

export interface WorkspaceEvent {
  type: string;
  status: string;
  updatedAt: Date;
  createdAt: Date;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class FirestoreEventBusService {
  private authService = inject(AuthService);
  // Optional: inject Firestore directly if registered in providers, but getFirestore() works if app is initialized
  private firestore: Firestore = getFirestore();
  private injector = inject(Injector);

  /**
   * Listen to collection-level changes for new/updated content events.
   * Path: workspace-events/{tenantId}/contents/{contentId}/events
   */
  listenToContentEvents(tenantId: string, contentId: string): Observable<WorkspaceEvent & { id: string }> {
    return runInInjectionContext(this.injector, () => {
      const colRef = collection(this.firestore, `workspace-events/${tenantId}/contents/${contentId}/events`);
      // Start listening to events created from *now* onwards
      const now = new Date();
      const q = query(colRef, where('createdAt', '>=', now), orderBy('createdAt', 'asc'));
      
      return (collectionData(q, { idField: 'id' }) as Observable<(WorkspaceEvent & { id: string })[]>).pipe(
        map((docs) => docs[docs.length - 1]), // Get the latest event pushed
        filter((doc) => !!doc),
        catchError((err) => {
          console.error('Firestore Event Bus Error:', err);
          return EMPTY;
        })
      );
    });
  }

  /**
   * Listen to content status document changes.
   * Path: workspace-events/{tenantId}/contents/{contentId}
   */
  listenToContentStatus(tenantId: string, contentId: string): Observable<any> {
    return runInInjectionContext(this.injector, () => {
      const docRef = doc(this.firestore, `workspace-events/${tenantId}/contents/${contentId}`);
      return (docData(docRef) as Observable<any>).pipe(
        catchError((err) => {
          console.error('Firestore Event Bus Status Error:', err);
          return EMPTY;
        })
      );
    });
  }
}
